<template>
  <div :class="$style.topbar">
    <div class="mr-auto d-flex align-items-center"></div>
    <div class="mr-4 mt-3 d-sm-block d-none">
      <p>
        Hello, <strong class="ml-1">{{ user.name ? user.name : 'Anonymous' }}</strong> as
        <strong>{{ getRoleLabel(user.role) }}</strong>
      </p>
    </div>
    <!-- Notification Bell -->
    <div class="mr-3">
      <a-dropdown :trigger="['click']" placement="bottomRight" @visibleChange="onDropdownVisible">
        <a-badge :count="totalUnreadCount" :overflow-count="99">
          <a-button type="text" class="notification-btn">
            <BellOutlined style="font-size: 20px" />
          </a-button>
        </a-badge>
        <template #overlay>
          <div class="notification-dropdown">
            <div class="notification-header">
              <span class="notification-title">Notifikasi</span>
              <a-button
                v-if="totalUnreadCount > 0"
                type="link"
                size="small"
                @click.stop="markAllRead"
              >
                Tandai semua dibaca
              </a-button>
            </div>
            <div v-if="!isMobileCms" class="notification-tabs" @click.stop>
              <a-tabs v-model:activeKey="activeTab">
                <a-tab-pane key="semua" tab="Semua">
                  <template #tab>
                    <span>
                      Semua
                      <a-badge
                        v-if="totalUnreadCount > 0"
                        :count="totalUnreadCount"
                        :overflow-count="99"
                        :offset="[10, 0]"
                      />
                    </span>
                  </template>
                  <div class="notification-list">
                    <a-spin v-if="loadingNotif" class="notification-loading" />
                    <template v-else-if="allNotifications.length > 0">
                      <div
                        v-for="notif in allNotifications"
                        :key="notif.id"
                        class="notification-item"
                        :class="{ unread: !notif.isRead }"
                        @click="handleNotificationClick(notif)"
                      >
                        <div class="notif-content">
                          <div class="notif-header">
                            <div class="notif-title">{{ notif.title }}</div>
                            <div class="notif-time">{{ formatTime(notif.updatedAt) }}</div>
                          </div>
                          <div class="notif-desc" v-html="sanitizeHtml(notif.description)"></div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="notification-empty">
                      <InboxOutlined style="font-size: 32px; color: #999" />
                      <p>Tidak ada notifikasi</p>
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane key="laporan" tab="Laporan">
                  <template #tab>
                    <span>
                      Laporan
                      <a-badge
                        v-if="laporanData.unreadCount > 0"
                        :count="laporanData.unreadCount"
                        :overflow-count="99"
                        :offset="[10, 0]"
                      />
                    </span>
                  </template>
                  <div class="notification-list">
                    <a-spin v-if="loadingNotif" class="notification-loading" />
                    <template v-else-if="limitedLaporan.length > 0">
                      <div
                        v-for="notif in limitedLaporan"
                        :key="notif.id"
                        class="notification-item"
                        :class="{ unread: !notif.isRead }"
                        @click="handleNotificationClick(notif)"
                      >
                        <div class="notif-content">
                          <div class="notif-header">
                            <div class="notif-title">{{ notif.title }}</div>
                            <div class="notif-time">{{ formatTime(notif.updatedAt) }}</div>
                          </div>
                          <div class="notif-desc" v-html="sanitizeHtml(notif.description)"></div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="notification-empty">
                      <InboxOutlined style="font-size: 32px; color: #999" />
                      <p>Tidak ada notifikasi laporan</p>
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane key="surat" tab="Surat">
                  <template #tab>
                    <span>
                      Surat
                      <a-badge
                        v-if="suratData.unreadCount > 0"
                        :count="suratData.unreadCount"
                        :overflow-count="99"
                        :offset="[10, 0]"
                      />
                    </span>
                  </template>
                  <div class="notification-list">
                    <a-spin v-if="loadingNotif" class="notification-loading" />
                    <template v-else-if="limitedSurat.length > 0">
                      <div
                        v-for="notif in limitedSurat"
                        :key="notif.id"
                        class="notification-item"
                        :class="{ unread: !notif.isRead }"
                        @click="handleNotificationClick(notif)"
                      >
                        <div class="notif-content">
                          <div class="notif-header">
                            <div class="notif-title">{{ notif.title }}</div>
                            <div class="notif-time">{{ formatTime(notif.updatedAt) }}</div>
                          </div>
                          <div class="notif-desc" v-html="sanitizeHtml(notif.description)"></div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="notification-empty">
                      <InboxOutlined style="font-size: 32px; color: #999" />
                      <p>Tidak ada notifikasi surat</p>
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane key="undangan" tab="Undangan">
                  <template #tab>
                    <span>
                      Undangan
                      <a-badge
                        v-if="undanganData.unreadCount > 0"
                        :count="undanganData.unreadCount"
                        :overflow-count="99"
                        :offset="[10, 0]"
                      />
                    </span>
                  </template>
                  <div class="notification-list">
                    <a-spin v-if="loadingNotif" class="notification-loading" />
                    <template v-else-if="limitedUndangan.length > 0">
                      <div
                        v-for="notif in limitedUndangan"
                        :key="notif.id"
                        class="notification-item"
                        :class="{ unread: !notif.isRead }"
                        @click="handleNotificationClick(notif)"
                      >
                        <div class="notif-content">
                          <div class="notif-header">
                            <div class="notif-title">{{ notif.title }}</div>
                            <div class="notif-time">{{ formatTime(notif.updatedAt) }}</div>
                          </div>
                          <div class="notif-desc" v-html="sanitizeHtml(notif.description)"></div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="notification-empty">
                      <InboxOutlined style="font-size: 32px; color: #999" />
                      <p>Tidak ada notifikasi undangan</p>
                    </div>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
            <div v-else class="notification-list" @click.stop>
              <a-spin v-if="loadingNotif" class="notification-loading" />
              <template v-else-if="allNotifications.length > 0">
                <div
                  v-for="notif in allNotifications"
                  :key="notif.id"
                  class="notification-item"
                  :class="{ unread: !notif.isRead }"
                  @click="handleNotificationClick(notif)"
                >
                  <div class="notif-content">
                    <div class="notif-header">
                      <div class="notif-title">{{ notif.title }}</div>
                      <div class="notif-time">{{ formatTime(notif.updatedAt) }}</div>
                    </div>
                    <div class="notif-desc" v-html="sanitizeHtml(notif.description)"></div>
                  </div>
                </div>
              </template>
              <div v-else class="notification-empty">
                <InboxOutlined style="font-size: 32px; color: #999" />
                <p>Tidak ada notifikasi</p>
              </div>
            </div>
            <div class="notification-footer">
              <a-button type="link" size="small" @click="navigateToNotificationsList">
                Tampilkan Semua
              </a-button>
            </div>
          </div>
        </template>
      </a-dropdown>
    </div>
    <div class="float-right">
      <vb-user-menu />
    </div>
  </div>
</template>

<script>
import VbUserMenu from './UserMenu'
import { useStore } from 'vuex'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getMenuData } from '@/services/menu'
import { notificationAPI } from '@/services/axios/perki'
import { BellOutlined, BellFilled, InboxOutlined } from '@ant-design/icons-vue'
import moment from 'moment'

export default {
  components: {
    VbUserMenu,
    BellOutlined,
    InboxOutlined,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const user = computed(() => store.getters['user/user'])
    const selectedKeys = ref([route.name])

    // Sanitize HTML to prevent XSS attacks while allowing safe formatting
    const sanitizeHtml = html => {
      if (!html) return ''

      // List of allowed tags
      const allowedTags = ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'ul', 'ol', 'li']

      // Remove dangerous tags and attributes
      const div = document.createElement('div')
      div.innerHTML = html

      // Remove script tags and their content
      const scripts = div.querySelectorAll('script')
      scripts.forEach(script => script.remove())

      // Remove iframe tags
      const iframes = div.querySelectorAll('iframe')
      iframes.forEach(iframe => iframe.remove())

      // Remove on* attributes (onclick, onerror, etc.)
      const allElements = div.querySelectorAll('*')
      allElements.forEach(el => {
        Array.from(el.attributes).forEach(attr => {
          if (attr.name.startsWith('on')) {
            el.removeAttribute(attr.name)
          }
        })
      })

      // Remove disallowed tags but keep their content
      const allTags = div.querySelectorAll('*')
      allTags.forEach(el => {
        if (!allowedTags.includes(el.tagName.toLowerCase())) {
          const parent = el.parentNode
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el)
          }
          parent.removeChild(el)
        }
      })

      return div.innerHTML
    }

    const menuData = getMenuData

    // Format role label
    const getRoleLabel = role => {
      if (!role) return 'Guest'

      const roleMap = {
        super_admin: 'Super Admin',
        admin: 'Admin',
        ketua: 'Ketua',
        sekretaris: 'Sekretaris',
        bendahara: 'Bendahara',
        pekerja_harian: 'Pekerja Harian',
        staff: 'Staff',
        user: 'User',
      }

      return (
        roleMap[role] ||
        role
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      )
    }

    // Notification state
    const isMobileCms = computed(() => route.path.startsWith('/mobile-cms'))
    const activeTab = ref('semua')
    const loadingNotif = ref(false)
    const undanganData = ref({ notifications: [], unreadCount: 0 })
    const laporanData = ref({ notifications: [], unreadCount: 0 })
    const suratData = ref({ notifications: [], unreadCount: 0 })
    const membershipData = ref({ notifications: [], unreadCount: 0 })
    const totalUnreadCount = ref(0)
    let refreshInterval = null

    // Limit notifications to 5 per category
    const limitedUndangan = computed(() => (undanganData.value.notifications || []).slice(0, 5))
    const limitedLaporan = computed(() => (laporanData.value.notifications || []).slice(0, 5))
    const limitedSurat = computed(() => (suratData.value.notifications || []).slice(0, 5))

    // Compute all notifications (limited to 20 total)
    const allNotifications = computed(() => {
      const all = [
        ...(undanganData.value.notifications || []),
        ...(laporanData.value.notifications || []),
        ...(suratData.value.notifications || []),
        ...(membershipData.value.notifications || []),
      ]
      // Sort by updatedAt descending
      return all.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, 20)
    })

    const fetchUnreadCount = async () => {
      try {
        const response = await notificationAPI.getUnreadCount()
        if (response.data?.status === 'success') {
          totalUnreadCount.value = response.data.data?.totalUnreadCount || 0
        }
      } catch (error) {
        console.error('Error fetching unread count:', error)
      }
    }

    const fetchNotifications = async () => {
      try {
        loadingNotif.value = true
        const response = await notificationAPI.getAll({ page: 1, pageSize: 20 })
        if (response.data?.status === 'success') {
          const data = response.data.data

          // Update category data
          undanganData.value = data.undangan || { notifications: [], unreadCount: 0 }
          laporanData.value = data.laporan || { notifications: [], unreadCount: 0 }
          suratData.value = data.surat || { notifications: [], unreadCount: 0 }
          membershipData.value = data.membership || { notifications: [], unreadCount: 0 }
          totalUnreadCount.value = data.totalUnreadCount || 0
        }
      } catch (error) {
        console.error('Error fetching notifications:', error)
      } finally {
        loadingNotif.value = false
      }
    }

    const onDropdownVisible = visible => {
      if (visible) {
        fetchNotifications()
      }
    }

    const handleNotificationClick = async notif => {
      // Mark as read if unread
      if (!notif.isRead) {
        try {
          await notificationAPI.markAsRead(notif.id)
          notif.isRead = true

          // Update unread counts
          if (notif.category === 'undangan') {
            undanganData.value.unreadCount = Math.max(0, undanganData.value.unreadCount - 1)
          } else if (notif.category === 'laporan') {
            laporanData.value.unreadCount = Math.max(0, laporanData.value.unreadCount - 1)
          } else if (notif.category === 'surat') {
            suratData.value.unreadCount = Math.max(0, suratData.value.unreadCount - 1)
          } else if (notif.category === 'membership') {
            membershipData.value.unreadCount = Math.max(0, membershipData.value.unreadCount - 1)
          }
          totalUnreadCount.value = Math.max(0, totalUnreadCount.value - 1)
        } catch (error) {
          console.error('Error marking notification as read:', error)
        }
      }

      // Navigate to the path
      if (notif.path) {
        if (notif.type === 'calendar_event' && notif.path.includes('http')) {
          return window.open(notif.path, '_blank', 'noopener,noreferrer')
        }
        router.push(notif.path)
      }
    }

    const markAllRead = async () => {
      try {
        await notificationAPI.markAllAsRead()

        // Update all notifications to read
        undanganData.value.notifications.forEach(n => (n.isRead = true))
        laporanData.value.notifications.forEach(n => (n.isRead = true))
        suratData.value.notifications.forEach(n => (n.isRead = true))

        // Reset unread counts
        undanganData.value.unreadCount = 0
        laporanData.value.unreadCount = 0
        suratData.value.unreadCount = 0
        totalUnreadCount.value = 0
      } catch (error) {
        console.error('Error marking all as read:', error)
      }
    }

    const formatTime = dateString => {
      if (!dateString) return ''
      const date = moment(dateString)
      const now = moment()

      if (now.diff(date, 'minutes') < 60) {
        return `${now.diff(date, 'minutes')} menit yang lalu`
      } else if (now.diff(date, 'hours') < 24) {
        return `${now.diff(date, 'hours')} jam yang lalu`
      } else if (now.diff(date, 'days') < 7) {
        return `${now.diff(date, 'days')} hari yang lalu`
      } else {
        return date.format('DD MMM YYYY HH:mm')
      }
    }

    const navigateToNotificationsList = () => {
      if (route.path.startsWith('/mobile-cms')) {
        router.push('/mobile-cms/notifications')
      } else {
        router.push('/notifications')
      }
    }

    const handleClick = e => {
      const item =
        menuData.find(m => m.key === e.key) ||
        menuData.flatMap(m => m.children || []).find(c => c.key === e.key)
      if (item && item.url) {
        router.push(item.url)
      }
    }

    onMounted(() => {
      // Fetch initial unread count and notifications
      fetchUnreadCount()
      fetchNotifications()
      console.log(user, '<<<<')
    })

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    })

    return {
      user,
      sanitizeHtml,
      selectedKeys,
      menuData,
      handleClick,
      getRoleLabel,
      // Notification
      isMobileCms,
      activeTab,
      loadingNotif,
      undanganData,
      laporanData,
      suratData,
      membershipData,
      totalUnreadCount,
      allNotifications,
      limitedUndangan,
      limitedLaporan,
      limitedSurat,
      onDropdownVisible,
      handleNotificationClick,
      markAllRead,
      formatTime,
      navigateToNotificationsList,
    }
  },
}
</script>

<style lang="scss" module>
@import './style.module.scss';
</style>

<style lang="scss" scoped>
.notification-btn {
  padding: 4px 8px;
  height: auto;
}

.notification-dropdown {
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  width: 360px;
  max-height: 480px;
  overflow: hidden;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.notification-title {
  font-weight: 600;
  font-size: 16px;
  color: #e10f28;
}

:deep(.ant-tabs-nav) {
  margin: 0 16px !important;
}

.notification-list {
  max-height: 320px;
  overflow-y: auto;
  border-top: 1px solid #f0f0f0;
}

.notification-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.notification-item {
  display: flex;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f5f5f5;

  &:hover {
    background: #fafafa;
  }

  &.unread {
    background: #efefff;

    &:hover {
      background: #e6e6ff;
    }
  }
}

.notif-content {
  flex: 1;
  min-width: 0;
}

.notif-header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.notif-title {
  font-weight: 700;
  font-size: 14px;
  color: #444294;
  line-height: 22px;
}

.notif-desc {
  font-size: 14px;
  color: #0b3334;
  margin-bottom: 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notif-time {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #8c8c8c;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
}

.notification-empty p {
  margin-top: 12px;
  margin-bottom: 0;
}

.notification-footer {
  padding: 12px 8px;
  border-top: 1px solid #f0f0f0;
}

:deep(.ant-badge) {
  .ant-badge-count {
    background: #e10f28;
  }
}

:deep(.ant-tabs-bar) {
  margin: 0 !important;
}

:deep(.ant-tabs-ink-bar) {
  background-color: #e10f28 !important;
}

:deep(.ant-tabs-nav .ant-tabs-tab) {
  font-weight: 400 !important;
}

:deep(.ant-tabs-nav .ant-tabs-tab-active) {
  color: #e10f28 !important;
  font-weight: 700 !important;
}
:deep(.ant-tabs-nav .ant-tabs-tab) {
  margin: 0 20px 0 0 !important;
}

:deep(.ant-tabs-nav .ant-tabs-tab:hover) {
  color: #e10f28 !important;
}
</style>
